import type { CodeableConcept } from "@medplum/fhirtypes";
import { CommandGroup, CommandItem } from "@repo/ui/components/ui/command";
import { useHpoCodeSearch } from "@/shared/hooks/use-hpo-code-search";
import { SearchPicker } from "@/shared/pickers/search-picker";

interface HpoCodePickerProps {
  value?: CodeableConcept;
  onChange: (value: CodeableConcept | undefined) => void;
  placeholder?: string;
}

/**
 * Symptom picker backed by the Human Phenotype Ontology (HPO) via NLM Clinical
 * Tables. Free, no auth. Returns codes like `HP:0002315` (Headache). Future:
 * swap underlying hook to SNOMED CT once Medplum's terminology service is
 * loaded — Observation.code shape stays identical, only the system URL changes.
 */
export function HpoCodePicker({ value, onChange, placeholder = "Search symptoms…" }: HpoCodePickerProps) {
  const displayText = value?.coding?.[0] ? `${value.coding[0].code} — ${value.coding[0].display}` : undefined;
  return (
    <SearchPicker.Root placeholder={placeholder}>
      <SearchPicker.Trigger
        label={displayText}
        onClear={value ? () => onChange(undefined) : undefined}
        clearLabel="Clear symptom"
      />
      <SearchPicker.Content>
        <HpoResults value={value} onChange={onChange} />
      </SearchPicker.Content>
    </SearchPicker.Root>
  );
}

function HpoResults({ value, onChange }: HpoCodePickerProps) {
  const { state, actions } = SearchPicker.useContext();
  const { options, isLoading } = useHpoCodeSearch(state.query);
  return (
    <SearchPicker.Search>
      <SearchPicker.Results isLoading={isLoading} count={options.length} emptyMessage="No codes found.">
        <CommandGroup>
          {options.map((option) => (
            <CommandItem
              key={option.code}
              value={option.code}
              onSelect={() => {
                onChange({ coding: [option.coding], text: option.display });
                actions.close();
              }}
              data-checked={value?.coding?.[0]?.code === option.code ? "true" : undefined}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">{option.code}</span>
                <span className="text-xs text-muted-foreground">{option.display}</span>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </SearchPicker.Results>
    </SearchPicker.Search>
  );
}
